import { useEffect, useState, type FormEvent } from "react";
import { useLantern, useSession } from "../hooks";
import type { Group, Principal, UUID } from "../sync/types";
import type { GroupMiss } from "../sync/api";
import { Avatar, EmptyState, Field, Form, Switch } from "./ui";
import { list as listOf, t, tn } from "../i18n";

// GroupsTab lists the workspace's user groups: an @handle that mentions
// everyone in it. Admins make, change and delete them here.
export function GroupsTab({ q }: { q: string }) {
  const session = useSession();
  const state = useLantern();
  const admin = state.me?.role === "admin";
  const [editing, setEditing] = useState<UUID | "new" | null>(null);
  // Who a saved group could not put in its channels.
  const [note, setNote] = useState("");
  useEffect(() => void session.loadGroups(), [session]);
  const done = (missed?: GroupMiss[]) => {
    setEditing(null);
    setNote(
      missed?.length
        ? t("Saved, but not everyone joined its channels: {list}", {
            list: listOf(missed.map((m) => t("{name} in {channel} ({why})", { name: session.name(m.principal_id), channel: session.channelName(m.channel_id), why: m.error }))),
          })
        : "",
    );
  };

  const query = q.trim().toLowerCase().replace(/^@/, "");
  const shown = state.groups.filter((g) => !query || g.handle.includes(query) || g.name.toLowerCase().includes(query));
  return (
    <>
      {admin && editing !== "new" && (
        <p>
          <button onClick={() => setEditing("new")}>{t("New group")}</button>
        </p>
      )}
      {note && (
        <p className="notice" role="status">
          {note}
        </p>
      )}
      {editing === "new" && <GroupEditor onDone={done} />}
      {shown.length === 0 && editing !== "new" ? (
        <EmptyState icon="👥" title={query ? t("No group matches “{q}”", { q: q.trim() }) : t("No groups yet")}>
          {!query && (admin ? t("Make one so a single @handle reaches a whole team, like @backend or @oncall.") : t("Admins can make groups, so a single @handle reaches a whole team."))}
        </EmptyState>
      ) : (
        <ul className="group-list">
          {shown.map((g) =>
            editing === g.id ? (
              <li key={g.id}>
                <GroupEditor group={g} onDone={done} />
              </li>
            ) : (
              <li key={g.id} className="card group">
                <div className="group-head">
                  <span className="group-text">
                    <strong>{g.name}</strong>
                    <span className="muted">
                      @{g.handle} · {tn(g.member_ids.length, "{n} person", "{n} people")}
                      {state.me && g.member_ids.includes(state.me.id) && t(" · you're in it")}
                    </span>
                    {g.description && <span className="group-description">{g.description}</span>}
                  </span>
                  {admin && (
                    <button className="small" onClick={() => setEditing(g.id)}>
                      {t("Edit")}
                    </button>
                  )}
                </div>
                {g.member_ids.length > 0 && (
                  <div className="group-members">
                    {g.member_ids.slice(0, 12).map((id) => (
                      <button key={id} className="group-member" data-profile={id} title={session.name(id)} aria-label={t("{name}'s profile", { name: session.name(id) })}>
                        <Avatar id={id} name={session.name(id)} size={28} />
                      </button>
                    ))}
                    {g.member_ids.length > 12 && <span className="muted">+{g.member_ids.length - 12}</span>}
                  </div>
                )}
              </li>
            ),
          )}
        </ul>
      )}
    </>
  );
}

// GroupEditor makes a group, or changes or deletes one.
function GroupEditor({ group, onDone }: { group?: Group; onDone: (missed?: GroupMiss[]) => void }) {
  const session = useSession();
  const [name, setName] = useState(group?.name ?? "");
  const [handle, setHandle] = useState(group?.handle ?? "");
  const [description, setDescription] = useState(group?.description ?? "");
  const [members, setMembers] = useState<UUID[]>(group?.member_ids ?? []);
  const [role, setRole] = useState(group?.role ?? "");
  const [channels, setChannels] = useState<UUID[]>(group?.channel_ids ?? []);
  const [requireMFA, setRequireMFA] = useState(group?.require_mfa ?? false);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const state = useLantern();
  const mine = [...state.channels.values()]
    .map((c) => c.channel)
    .filter((c) => (c.kind === "public" || c.kind === "private") && !c.archived_at)
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  const query = q.trim().toLowerCase().replace(/^@/, "");
  const matches = (p: Principal) => p.handle.toLowerCase().includes(query) || p.display_name.toLowerCase().includes(query);
  const candidates = query
    ? [...session.directory.values()].filter((p) => p.status === "active" && p.kind === "user" && !members.includes(p.id) && matches(p)).slice(0, 6)
    : [];

  async function run(f: () => Promise<unknown>) {
    setError("");
    setBusy(true);
    try {
      const r = (await f()) as { not_added?: GroupMiss[] } | undefined;
      await session.loadGroups();
      onDone(r?.not_added);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  function save(e: FormEvent) {
    e.preventDefault();
    const g = { name: name.trim(), handle: handle.trim().replace(/^@/, ""), description: description.trim(), member_ids: members, role, channel_ids: channels, require_mfa: requireMFA };
    return run(() => (group ? session.api.updateGroup(group.id, g) : session.api.createGroup(g)));
  }

  return (
    <Form className="card group-editor" onSubmit={save} aria-label={group ? t("Edit {name}", { name: group.name }) : t("New group")}>
      <h3>{group ? t("Edit {name}", { name: group.name }) : t("New group")}</h3>
      <Field label={t("Name")}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("Backend team")} required autoFocus maxLength={80} />
      </Field>
      <Field label={t("Handle")} hint={t("Mention it as @handle to notify everyone in the group who is in the channel.")}>
        <input
          value={handle}
          onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/\s+/g, "-"))}
          placeholder="backend"
          required
          maxLength={64}
          spellCheck={false}
        />
      </Field>
      <Field label={t("Description")}>
        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("Who they are and when to ask them")} maxLength={250} />
      </Field>
      <Field label={t("Role it gives")} hint={t("Given to each member when they join, or to all when you change it, whether higher or lower: a Guest group makes its members guests. The most powerful of someone's groups wins; leaving a group keeps the role.")}>
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">{t("None: roles stay as they are")}</option>
          <option value="member">{t("Member")}</option>
          <option value="guest">{t("Guest")}</option>
          <option value="admin">{t("Admin")}</option>
        </select>
      </Field>
      <Field label={t("Channels its members join")} hint={t("Channels you are in. Members join them, and so does anyone added later; leaving the group leaves them in.")}>
        <div className="invite-channels" role="group" aria-label={t("Channels its members join")}>
          {mine.map((c) => (
            <label key={c.id} className="check">
              <input type="checkbox" checked={channels.includes(c.id)} onChange={(e) => setChannels(e.target.checked ? [...channels, c.id] : channels.filter((x) => x !== c.id))} />
              {c.kind === "private" ? "🔒 " : "#"}
              {c.name}
            </label>
          ))}
        </div>
      </Field>
      <Switch
        checked={requireMFA}
        onChange={setRequireMFA}
        label={t("Require two-step verification")}
        description={t("Members with a local password set up an authenticator app or a passkey before they can use Lantern. Those with single sign-on only are left to the identity provider.")}
      />
      <Field label={t("Members · {n}", { n: members.length })}>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Add people by name or @handle")} aria-label={t("Add people")} />
      </Field>
      {candidates.length > 0 && (
        <ul className="rows" aria-label={t("Add to group")}>
          {candidates.map((p) => (
            <li key={p.id}>
              <Avatar id={p.id} name={p.display_name || p.handle} size={28} />
              <span className="row-main">
                <span className="row-title">{p.display_name || p.handle}</span>
                <span className="row-meta">@{p.handle}</span>
              </span>
              <button
                type="button"
                className="small"
                onClick={() => {
                  setMembers((m) => [...m, p.id]);
                  setQ("");
                }}
              >
                {t("Add")}
              </button>
            </li>
          ))}
        </ul>
      )}
      {members.length > 0 && (
        <ul className="rows" aria-label={t("Members")}>
          {members.map((id) => {
            const handle = session.directory.get(id)?.handle;
            return (
              <li key={id}>
                <Avatar id={id} name={session.name(id)} size={28} />
                <span className="row-main">
                  <span className="row-title">{session.name(id)}</span>
                  <span className="row-meta">{handle && "@" + handle}</span>
                </span>
                <button type="button" className="small ghost" onClick={() => setMembers((m) => m.filter((x) => x !== id))} aria-label={t("Remove {name}", { name: session.name(id) })}>
                  {t("Remove")}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {error && <p className="error">{error}</p>}
      <div className="form-actions">
        {group && (
          <button
            type="button"
            className="danger"
            disabled={busy}
            onClick={() => window.confirm(t("Delete @{handle}? Mentions of it in old messages stay as text.", { handle: group.handle })) && void run(() => session.api.deleteGroup(group.id))}
          >
            {t("Delete group")}
          </button>
        )}
        <button type="button" onClick={() => onDone()} disabled={busy}>
          {t("Cancel")}
        </button>
        <button type="submit" disabled={busy || !name.trim() || !handle.trim()}>
          {group ? t("Save") : t("Create group")}
        </button>
      </div>
    </Form>
  );
}
